// HoaListPage — /hoas route (§8.2 HOA list page).
//
// Lists all HOAs from GET /api/hoas; "New HOA" button → Dialog → POST.
// Delete with confirmation ConfirmDialog → on 409, shows inline error.
//
// Owner-created review queue: HOAs with reviewStatus 'needs_review' (created via the
// owner-intake front door) surface in a "New — from owner intake" TODO section with
// Confirm (clear the flag) + Merge into… (fold an accidental duplicate into the real
// HOA) actions.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import {
  listHoas,
  createHoa,
  deleteHoa,
  confirmHoa,
  mergeHoaInto,
  type HoaWithCount,
} from '@/lib/hoasClient';
import { ApiError } from '@/lib/api';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { useFocusTrap } from '@/lib/useFocusTrap';
import { useEscapeKey } from '@/lib/useEscapeKey';
import { useInertBackground } from '@/lib/useInertBackground';

/** True when the HOA is an owner-created entry still awaiting staff review. */
function needsReview(hoa: HoaWithCount): boolean {
  return hoa.reviewStatus === 'needs_review';
}

export function HoaListPage() {
  const navigate = useNavigate();
  const [hoas, setHoas] = useState<HoaWithCount[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // New HOA dialog
  const [showNewDialog, setShowNewDialog] = useState(false);
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const newNameInputRef = useRef<HTMLInputElement>(null);

  // Delete confirmation
  const [deleteTarget, setDeleteTarget] = useState<HoaWithCount | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Owner-created review queue actions
  const [confirmingId, setConfirmingId] = useState<string | null>(null);
  const [reviewError, setReviewError] = useState<string | null>(null);
  // Merge dialog: the source (owner-created) HOA + the chosen target.
  const [mergeSource, setMergeSource] = useState<HoaWithCount | null>(null);
  const [mergeTargetId, setMergeTargetId] = useState('');
  const [merging, setMerging] = useState(false);
  const [mergeError, setMergeError] = useState<string | null>(null);

  const reviewQueue = useMemo(() => hoas.filter(needsReview), [hoas]);
  // Merge targets = every OTHER HOA (you can't merge a HOA into itself).
  const mergeTargets = useMemo(
    () => hoas.filter((h) => h.hoaId !== mergeSource?.hoaId),
    [hoas, mergeSource],
  );

  useEffect(() => {
    void loadHoas();
  }, []);

  // Focus the input on open. Focus trap + return + Escape stack are wired
  // via shared hooks below so nested modals don't collide.
  useEffect(() => {
    if (!showNewDialog) return;
    newNameInputRef.current?.focus();
  }, [showNewDialog]);

  const handleEscapeNewDialog = useCallback(() => {
    if (!creating) setShowNewDialog(false);
  }, [creating]);
  useEscapeKey(handleEscapeNewDialog, showNewDialog);
  useInertBackground(showNewDialog);
  const newDialogRef = useFocusTrap(showNewDialog);

  const closeMergeDialog = useCallback(() => {
    if (!merging) {
      setMergeSource(null);
      setMergeTargetId('');
      setMergeError(null);
    }
  }, [merging]);
  useEscapeKey(closeMergeDialog, !!mergeSource);
  useInertBackground(!!mergeSource);
  const mergeDialogRef = useFocusTrap(!!mergeSource);

  async function loadHoas() {
    setLoading(true);
    setError(null);
    try {
      const data = await listHoas();
      setHoas(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load HOAs');
    } finally {
      setLoading(false);
    }
  }

  async function handleCreate() {
    if (!newName.trim()) return;
    setCreating(true);
    setCreateError(null);
    try {
      const hoa = await createHoa(newName.trim());
      setShowNewDialog(false);
      setNewName('');
      navigate(`/hoas/${hoa.hoaId}`);
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : 'Failed to create HOA');
    } finally {
      setCreating(false);
    }
  }

  async function handleDelete() {
    if (!deleteTarget) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await deleteHoa(deleteTarget.hoaId);
      setDeleteTarget(null);
      await loadHoas();
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        const body = err.body as { error?: string } | null;
        setDeleteError(body?.error ?? 'This HOA has associated properties. Remove all associations first.');
      } else {
        setDeleteError(err instanceof Error ? err.message : 'Failed to delete HOA');
      }
    } finally {
      setDeleting(false);
    }
  }

  async function handleConfirm(hoa: HoaWithCount) {
    setConfirmingId(hoa.hoaId);
    setReviewError(null);
    try {
      await confirmHoa(hoa.hoaId);
      await loadHoas();
    } catch (err) {
      setReviewError(
        err instanceof Error ? err.message : 'Failed to confirm this HOA. Please try again.',
      );
    } finally {
      setConfirmingId(null);
    }
  }

  async function handleMerge() {
    if (!mergeSource || !mergeTargetId) return;
    setMerging(true);
    setMergeError(null);
    try {
      await mergeHoaInto(mergeSource.hoaId, mergeTargetId);
      setMergeSource(null);
      setMergeTargetId('');
      await loadHoas();
    } catch (err) {
      if (err instanceof ApiError) {
        const body = err.body as { error?: string } | null;
        setMergeError(body?.error ?? 'Failed to merge this HOA. Please try again.');
      } else {
        setMergeError(err instanceof Error ? err.message : 'Failed to merge this HOA.');
      }
    } finally {
      setMerging(false);
    }
  }

  return (
    <div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 'var(--space-7)',
        }}
      >
        <div>
          <h1 style={{ fontSize: 'var(--text-xl)', fontWeight: 'var(--weight-bold)', color: 'var(--fg)', margin: 0 }}>
            HOAs
          </h1>
          <p style={{ fontSize: 'var(--text-sm)', color: 'var(--fg-muted)', marginTop: 'var(--space-2)' }}>
            Manage homeowner associations and their CC&R documents.
          </p>
        </div>
        <button
          type="button"
          className="btn"
          onClick={() => { setShowNewDialog(true); setNewName(''); setCreateError(null); }}
        >
          New HOA
        </button>
      </div>

      {loading && <p style={{ color: 'var(--fg-muted)' }}>Loading…</p>}
      {error && (
        <div className="alert" data-tone="danger">
          <div className="alert-body">{error}</div>
        </div>
      )}

      {reviewError && (
        <div className="alert" data-tone="danger" style={{ marginBottom: 'var(--space-5)' }}>
          <div className="alert-body">{reviewError}</div>
        </div>
      )}

      {/* Owner-intake review queue — HOAs an owner created that need a staff decision. */}
      {!loading && reviewQueue.length > 0 && (
        <section
          className="card"
          style={{ marginBottom: 'var(--space-7)', borderColor: 'var(--warn-border, var(--border))' }}
        >
          <div className="card-header" style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
            <span className="pill" data-tone="warn">Needs review</span>
            <h2 className="card-title" style={{ margin: 0 }}>New — from owner intake, confirm or merge</h2>
          </div>
          <p style={{ fontSize: 'var(--text-sm)', color: 'var(--fg-muted)', margin: '0 0 var(--space-4)' }}>
            An owner named these HOAs and uploaded their CC&amp;Rs. They&apos;re live, but please
            confirm each one — or, if it duplicates an HOA we already have, merge it into the
            real one.
          </p>
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Properties</th>
                  <th>CC&amp;R</th>
                  <th>Created</th>
                  <th className="col-action" aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {reviewQueue.map((hoa) => (
                  <tr key={hoa.hoaId}>
                    <td style={{ fontWeight: 'var(--weight-medium)' }}>{hoa.name}</td>
                    <td className="num">{hoa.propertyCount}</td>
                    <td>
                      {hoa.latestCcrVersionId ? (
                        <span className="pill" data-tone="info">On file</span>
                      ) : (
                        <span style={{ color: 'var(--fg-subtle)', fontSize: 'var(--text-xs)' }}>None</span>
                      )}
                    </td>
                    <td style={{ color: 'var(--fg-muted)' }}>
                      {new Date(hoa.createdAt).toLocaleDateString()}
                    </td>
                    <td className="col-action">
                      <div style={{ display: 'flex', gap: 'var(--space-3)', justifyContent: 'flex-end' }}>
                        <button
                          type="button"
                          className="btn"
                          data-variant="secondary"
                          data-size="sm"
                          onClick={() => navigate(`/hoas/${hoa.hoaId}`)}
                        >
                          Review
                        </button>
                        <button
                          type="button"
                          className="btn"
                          data-size="sm"
                          disabled={confirmingId === hoa.hoaId}
                          onClick={() => void handleConfirm(hoa)}
                        >
                          {confirmingId === hoa.hoaId ? 'Confirming…' : 'Confirm'}
                        </button>
                        <button
                          type="button"
                          className="btn"
                          data-variant="secondary"
                          data-size="sm"
                          onClick={() => { setMergeSource(hoa); setMergeTargetId(''); setMergeError(null); }}
                        >
                          Merge into…
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {!loading && !error && hoas.length === 0 && (
        <div className="card" style={{ textAlign: 'center', color: 'var(--fg-muted)' }}>
          No HOAs yet. Click "New HOA" to create one.
        </div>
      )}

      {!loading && hoas.length > 0 && (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Properties</th>
                <th>CC&amp;R</th>
                <th>Last Updated</th>
                <th className="col-action" aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {hoas.map((hoa) => (
                <tr key={hoa.hoaId}>
                  <td style={{ fontWeight: 'var(--weight-medium)' }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                      {hoa.name}
                      {needsReview(hoa) && (
                        <span className="pill" data-tone="warn" title="Created by an owner — awaiting staff review">
                          Needs review
                        </span>
                      )}
                    </span>
                  </td>
                  <td className="num">{hoa.propertyCount}</td>
                  <td>
                    {hoa.latestCcrVersionId ? (
                      <span className="pill" data-tone="info">On file</span>
                    ) : (
                      <span style={{ color: 'var(--fg-subtle)', fontSize: 'var(--text-xs)' }}>None</span>
                    )}
                  </td>
                  <td style={{ color: 'var(--fg-muted)' }}>
                    {new Date(hoa.updatedAt).toLocaleDateString()}
                  </td>
                  <td className="col-action">
                    <div style={{ display: 'flex', gap: 'var(--space-3)', justifyContent: 'flex-end' }}>
                      <button
                        type="button"
                        className="btn"
                        data-variant="secondary"
                        data-size="sm"
                        onClick={() => navigate(`/hoas/${hoa.hoaId}`)}
                      >
                        View
                      </button>
                      <button
                        type="button"
                        className="btn"
                        data-variant="danger-ghost"
                        data-size="sm"
                        onClick={() => { setDeleteTarget(hoa); setDeleteError(null); }}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* New HOA Dialog — DS .modal primitive composed inline (single use).
          Portaled to <body> so the inert-background hook can disable .app-shell
          without disabling the modal itself. */}
      {showNewDialog && createPortal(
        <>
          <div
            className="modal-backdrop"
            onClick={() => { if (!creating) setShowNewDialog(false); }}
          />
          <div
            ref={newDialogRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="new-hoa-title"
            className="modal"
            data-size="sm"
          >
            <div className="modal-header">
              <h2 id="new-hoa-title" className="modal-title">New HOA</h2>
            </div>
            <div className="modal-body">
              <div className="field">
                <label htmlFor="new-hoa-name">HOA name</label>
                <input
                  id="new-hoa-name"
                  ref={newNameInputRef}
                  className="input"
                  placeholder="e.g. Parkview HOA"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') void handleCreate(); }}
                />
              </div>
              {createError && (
                <div className="alert" data-tone="danger" style={{ marginTop: 'var(--space-5)' }}>
                  <svg
                    className="alert-icon"
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <circle cx="12" cy="12" r="10" />
                    <line x1="15" y1="9" x2="9" y2="15" />
                    <line x1="9" y1="9" x2="15" y2="15" />
                  </svg>
                  <div className="alert-body">{createError}</div>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn"
                data-variant="ghost"
                onClick={() => setShowNewDialog(false)}
                disabled={creating}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn"
                onClick={() => void handleCreate()}
                disabled={creating || !newName.trim()}
              >
                {creating ? 'Creating…' : 'Create'}
              </button>
            </div>
          </div>
        </>,
        document.body,
      )}

      {/* Merge dialog — pick the target HOA to fold the owner-created source into. */}
      {mergeSource && createPortal(
        <>
          <div className="modal-backdrop" onClick={closeMergeDialog} />
          <div
            ref={mergeDialogRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="merge-hoa-title"
            className="modal"
            data-size="sm"
          >
            <div className="modal-header">
              <h2 id="merge-hoa-title" className="modal-title">Merge &ldquo;{mergeSource.name}&rdquo; into…</h2>
            </div>
            <div className="modal-body">
              <p style={{ fontSize: 'var(--text-sm)', color: 'var(--fg-muted)', margin: '0 0 var(--space-4)' }}>
                Moves this HOA&apos;s CC&amp;Rs and property links into the target HOA, then
                deletes &ldquo;{mergeSource.name}&rdquo;. Use this when an owner accidentally
                created a duplicate of an HOA we already have.
              </p>
              <div className="field">
                <label htmlFor="merge-target">Merge into</label>
                <select
                  id="merge-target"
                  className="input"
                  value={mergeTargetId}
                  onChange={(e) => setMergeTargetId(e.target.value)}
                >
                  <option value="">Select the HOA to keep…</option>
                  {mergeTargets.map((h) => (
                    <option key={h.hoaId} value={h.hoaId}>
                      {h.name}{h.reviewStatus === 'needs_review' ? ' (needs review)' : ''}
                    </option>
                  ))}
                </select>
              </div>
              {mergeError && (
                <div className="alert" data-tone="danger" style={{ marginTop: 'var(--space-5)' }}>
                  <div className="alert-body">{mergeError}</div>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn"
                data-variant="ghost"
                onClick={closeMergeDialog}
                disabled={merging}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn"
                onClick={() => void handleMerge()}
                disabled={merging || !mergeTargetId}
              >
                {merging ? 'Merging…' : 'Merge'}
              </button>
            </div>
          </div>
        </>,
        document.body,
      )}

      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(open) => { if (!open) { setDeleteTarget(null); setDeleteError(null); } }}
        title={deleteTarget ? `Delete ${deleteTarget.name}?` : 'Delete HOA?'}
        description="This will permanently delete the HOA and all associated CC&R records. This action cannot be undone."
        confirmLabel="Delete"
        busyLabel="Deleting…"
        busy={deleting}
        error={deleteError}
        onConfirm={() => void handleDelete()}
      />
    </div>
  );
}
